import { SlideshowSelectEvent } from '@theme/events';

/**
 * Pagination dots for resource list carousels.
 *
 * These carousels advance a whole page at a time — one step moves by the number of
 * slides currently visible — so one dot per slide would not match what a click does.
 * This renders one dot per page and rebuilds itself when the visible slide count
 * changes at a breakpoint.
 *
 * Markup is generated here rather than in Liquid because the page count is only known
 * once the slides have been laid out.
 *
 * @example
 * <carousel-dots></carousel-dots>
 */
class CarouselDots extends HTMLElement {
  /** @type {any} */
  #slideshow = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #visibilityObserver = null;

  /** @type {number} */
  #perPage = 0;

  /** @type {HTMLButtonElement[]} */
  #dots = [];

  /** @type {number | null} */
  #frame = null;

  /** @type {boolean} */
  #rendered = false;

  async connectedCallback() {
    const slideshow = this.closest('slideshow-component');
    if (!slideshow) return;

    await customElements.whenDefined('slideshow-component');
    if (!this.isConnected) return;

    this.#slideshow = /** @type {any} */ (slideshow);

    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);

    this.#resizeObserver = new ResizeObserver(this.#scheduleRefresh);
    this.#resizeObserver.observe(slideshow);

    // The slideshow tracks visible slides with an IntersectionObserver and reflects the
    // result onto each slide's aria-hidden attribute. Watching that keeps the page count
    // in sync without duplicating the observer. The transitioning attribute is watched
    // too, so the count is re-checked as soon as a scroll settles.
    this.#visibilityObserver = new MutationObserver(this.#scheduleRefresh);
    this.#visibilityObserver.observe(slideshow, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'transitioning'],
      subtree: true,
    });

    this.#awaitFirstRender();
  }

  disconnectedCallback() {
    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSelect);
    this.#resizeObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    if (this.#frame != null) cancelAnimationFrame(this.#frame);

    this.#rendered = false;
    this.#slideshow = null;
    this.#resizeObserver = null;
    this.#visibilityObserver = null;
    this.#frame = null;
  }

  /** @type {HTMLElement[]} */
  get #slides() {
    return this.#slideshow?.slides ?? [];
  }

  /**
   * How many slides move per step, matching the slideshow's own paging. Zero until the
   * slideshow has worked out which slides are visible.
   * @type {number}
   */
  get #visibleCount() {
    return this.#slideshow?.visibleSlides?.length ?? 0;
  }

  /**
   * Keeps looking for the first usable layout, in case the slideshow settles without
   * mutating any slide attributes for the observers to pick up.
   * @param {number} [attempts] - How many frames are left to try.
   */
  #awaitFirstRender(attempts = 60) {
    if (!this.isConnected || this.#rendered) return;

    this.#refresh();

    if (this.#rendered || attempts <= 0) return;

    requestAnimationFrame(() => this.#awaitFirstRender(attempts - 1));
  }

  #scheduleRefresh = () => {
    if (this.#frame != null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#refresh();
    });
  };

  /**
   * Rebuilds the dots when the page count changed, then syncs the active one.
   */
  #refresh() {
    const perPage = this.#visibleCount;

    // The page count is unknown until the slideshow reports its visible slides, which it
    // does asynchronously. Guessing meanwhile would flash one dot per slide on page load,
    // so stay hidden and wait.
    if (perPage === 0) return;

    // Mid-scroll the leading and trailing slides fall below the slideshow's visibility
    // threshold, so it reports fewer visible slides than a settled page holds — three of
    // twelve becomes two, and the dots would rebuild from four to six and back on every
    // step. The current slide is just as unreliable while the scroll is in flight, so
    // leave the dots alone entirely until it settles.
    if (this.#rendered && this.#transitioning) return;

    const total = this.#slides.length;
    const pageCount = Math.ceil(total / perPage);

    if (perPage !== this.#perPage || pageCount !== this.#dots.length) {
      this.#perPage = perPage;
      this.#render(pageCount);
    }

    this.#syncActiveDot();
  }

  /**
   * @param {number} pageCount - The number of pages to render dots for.
   */
  #render(pageCount) {
    this.#rendered = true;
    this.#dots = [];
    this.replaceChildren();
    this.hidden = pageCount <= 1;

    if (this.hidden) return;

    const template = this.getAttribute('aria-label-template') ?? 'Slide %index% of %length%';
    const list = document.createElement('ol');
    list.className = 'carousel-dots__list';

    for (let page = 0; page < pageCount; page++) {
      const item = document.createElement('li');
      const dot = document.createElement('button');

      dot.type = 'button';
      dot.className = 'carousel-dots__dot';
      dot.setAttribute(
        'aria-label',
        template.replace('%index%', String(page + 1)).replace('%length%', String(pageCount))
      );
      dot.addEventListener('click', (event) => this.#select(page, event));

      item.append(dot);
      list.append(item);
      this.#dots.push(dot);
    }

    this.append(list);
  }

  /**
   * Moves the carousel to the first slide of the given page.
   * @param {number} page - The 0-based page index.
   * @param {Event} event - The originating click.
   */
  #select(page, event) {
    const slideshow = this.#slideshow;
    if (!slideshow) return;

    const index = Math.min(page * this.#perPage, this.#slides.length - 1);

    slideshow.select(index, event);
  }

  /** @type {boolean} */
  get #transitioning() {
    return this.#slideshow?.hasAttribute('transitioning') ?? false;
  }

  #handleSelect = (/** @type {any} */ event) => {
    if (event.target !== this.#slideshow) return;

    // A 'select' carries the slide the carousel is heading for, so the highlight can move
    // right away. A 'scroll' only reports where the scroll happens to be: mid-flight that
    // is a slide of the page being left or of the one after it, which made the highlight
    // jump to a neighbouring dot and back. Those are worth reading only once the scroll
    // has settled.
    if (event.detail?.trigger === 'scroll' && this.#transitioning) return;

    this.#syncActiveDot();
  };

  #syncActiveDot() {
    if (!this.#dots.length || !this.#slideshow) return;

    const total = this.#slides.length;
    const current = this.#slideshow.current ?? 0;

    // The last page is short whenever the slide count is not a multiple of the page
    // size: the scroller clamps, so `current` never reaches its first slide.
    const page =
      current + this.#perPage >= total
        ? this.#dots.length - 1
        : Math.min(Math.floor(current / this.#perPage), this.#dots.length - 1);

    this.#dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', String(index === page));
    });
  }
}

if (!customElements.get('carousel-dots')) {
  customElements.define('carousel-dots', CarouselDots);
}
